import { Save } from "@mui/icons-material";
import CancelIcon from "@mui/icons-material/Cancel";
import { CircularProgress, Typography } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import { StyledDropzone } from "../../styled/Dropzone";
import RequeridoLabel from "../labels/RequeridoLabel";
import DropzoneFileAttachment from "./DropzoneFileAttachment";

const DropZone = ({
  onUpload,
  file,
  setFile,
  accept,
  extension,
  loading,
  dropZoneText,
  dropZoneEditText,
  maxFiles,
  colorearArchivosAdjuntos,
  required,
  showButtonsAlways,
}) => {
  /**
   * @param {Object[]} archivosSeleccionadosUsuario - El array que contiene los archivos
   * seleccionados por el usuario en el explorador de archivos.
   */
  const onDropAccepted = async (archivosSeleccionadosUsuario) => {
    let archivosCargados = file ?? [];
    const readers = [];

    const superaLimiteArchivosAdjuntos =
      // Cuando maxFiles === 1 no contamos que se supera el limite
      // de archivos adjuntos. Se mantiene el comportamiento de
      // sobreescribir el archivo ya adjunto.
      maxFiles !== 1 && maxFiles !== 0 &&
      (archivosSeleccionadosUsuario.length > maxFiles ||
        // Cuando ya tiene 3 adjuntos y, por ejemplo, carga 5 más cuando el máximo es 4
        archivosSeleccionadosUsuario.length >
        maxFiles - archivosCargados.length ||
        archivosCargados.length === maxFiles);

    if (superaLimiteArchivosAdjuntos) {
      const archivosMaximo = maxFiles > 1 ? "archivos" : "archivo";
      const archivosCargadosString =
        archivosCargados.length > 1 ? "archivos" : "archivo";
      const archivosSeleccionados =
        archivosSeleccionadosUsuario.length > 1 ? "archivos" : "archivo";
      const cargadosPluralOSingular =
        archivosCargadosString === "archivos" ? "cargados" : "cargado";

      toast.error(
        `Puede adjuntar hasta ${maxFiles} ${archivosMaximo}. Ya tiene ${cargadosPluralOSingular} ${archivosCargados.length} ${archivosCargadosString} y ha seleccionado ${archivosSeleccionadosUsuario.length} ${archivosSeleccionados} más`
      );
      return;
    }

    if (maxFiles !== 0 && (maxFiles === 1 || !maxFiles)) {
      const f = archivosSeleccionadosUsuario[0];
      const reader = new FileReader();

      reader.readAsDataURL(f);

      reader.onload = () => {
        const index = reader.result.indexOf("base64,") + 7;
        archivosCargados = archivosCargados.toSpliced(0, 1, {
          filename: f.name,
          value: reader.result.substring(index),
          base64Header: reader.result.substring(0, index),
        });
        setFile(archivosCargados);
      };
      reader.onerror = function () {
        toast.error("Archivo inválido");
      };
    } else {
      archivosSeleccionadosUsuario.forEach((archivo) => {
        const reader = new FileReader();
        readers.push(reader);

        reader.readAsDataURL(archivo);

        reader.onload = function () {
          const index = reader.result.indexOf("base64,") + 7;
          const archivoYaAdjuntado = archivosCargados.findIndex(
            (a) => a.filename === archivo.name
          );
          if (archivoYaAdjuntado !== -1) {
            archivosCargados = archivosCargados.toSpliced(
              archivoYaAdjuntado,
              1,
              {
                filename: archivo.name,
                value: reader.result.substring(index),
                base64Header: reader.result.substring(0, index),
              }
            );
          } else {
            archivosCargados = archivosCargados.toSpliced(0, 0, {
              filename: archivo.name,
              value: reader.result.substring(index),
              base64Header: reader.result.substring(0, index),
            });
          }

          setFile(archivosCargados);
        };

        reader.onerror = () => {
          toast.error("Archivo inválido");
        };
      });
    }
  };

  const onDropRejected = (error) => {
    error[0].errors.forEach((error) => {
      switch (error.code) {
        case "file-invalid-type":
          toast.error(`La extensión del archivo debe ser ${extension}`);
          break;
        case "too-many-files":
          toast.error(`Máximo ${maxFiles} archivos`);
          break;
        default:
          toast.error("Archivo inválido");
          break;
      }
    });
  };

  const handleQuitarArchivo = (event, archivo) => {
    // Este stopPropagation() es necesario para impedir que el "click"
    // sea interpretado como que queremos abrir el dialog de importar archivos.
    event.stopPropagation();
    const index = file.findIndex((f) => f.filename === archivo.filename);
    const nuevaListaAdjuntos = file.toSpliced(index, 1);
    setFile(nuevaListaAdjuntos);
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDropAccepted,
    onDropRejected,
    maxFiles: maxFiles ?? 1,
    multiple: (maxFiles > 1 || maxFiles === 0) ? true : false,
    accept: accept,
  });

  return (
    <StyledDropzone.Container>
      <StyledDropzone.Dropzone {...getRootProps()}
        isDragActive={isDragActive}
        isDragAccept={isDragAccept}
        isDragReject={isDragReject}
      >
        <input {...getInputProps()} />
        {file?.length > 0 ? (
          <>
            <StyledDropzone.FileContainer>
              {file.map((f) => (
                <DropzoneFileAttachment
                  key={f.filename}
                  filename={f.filename}
                  adjuntosConColor={colorearArchivosAdjuntos}
                  quitarArchivo={(event) => handleQuitarArchivo(event, f)}
                />
              ))}
            </StyledDropzone.FileContainer>
            <Typography variant="caption" sx={{ textAlign: "center" }}>
              {dropZoneEditText}
            </Typography>
          </>
        ) : (
          <Typography variant="subtitle1">{dropZoneText}</Typography>
        )}
      </StyledDropzone.Dropzone>
      {required && (!file || file.length === 0) && (
        <div>
          <RequeridoLabel text={required || "Es obligatorio ingresar al menos un archivo."} />
        </div>
      )}
      {(file?.length > 0 || showButtonsAlways) && (
        <div>
          <StyledDropzone.Button
            variant="contained"
            color="primary"
            startIcon={<CancelIcon />}

            onClick={() => {
              setFile(null);
            }}
            disabled={loading}
          >
            Cancelar
          </StyledDropzone.Button>
          {onUpload && (
            <StyledDropzone.Button
              variant="contained"
              color="primary"
              startIcon={<Save />}
              onClick={onUpload}
              disabled={loading || (required && (!file || file.length === 0))}
            >
              {loading ? (
                <CircularProgress size={24} thickness={4} color="secondary" />
              ) : (
                "Enviar"
              )}
            </StyledDropzone.Button>
          )}
        </div>
      )}
    </StyledDropzone.Container>
  );
};

export default DropZone;
